import React, { useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import SharedLocationForm from '../vedic/forms/LocationForm';

/* ─── Animations ─── */
const fadeIn = keyframes`from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:translateY(0)}`;
const pulse = keyframes`0%{transform:scale(1)}50%{transform:scale(1.04)}100%{transform:scale(1)}`;

/* ─── Styled Components ─── */
const PageContainer = styled.div`max-width:1200px;margin:40px auto;padding:0 20px;`;
const PageTitle = styled.h1`font-size:2rem;text-align:center;margin-bottom:5px;color:${p => p.theme.colors.text.primary};`;
const PageSubtitle = styled.p`text-align:center;color:${p => p.theme.colors.text.secondary};margin-bottom:30px;font-size:0.95rem;`;

const TopGrid = styled.div`
  display:grid;grid-template-columns:1fr 1fr;gap:30px;
  @media(max-width:992px){grid-template-columns:1fr}
`;

const Card = styled.div`
  background:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.md}px;box-shadow: ${p => p.theme.shadow.card};padding:30px;
  animation:${fadeIn} 0.3s ease;
`;

const CardTitle = styled.h2`font-size:1.3rem;margin-bottom:20px;color:${p => p.theme.colors.text.primary};text-align:center;`;

const Select = styled.select`
  width:100%;padding:10px 12px;border:1px solid ${p => p.theme.colors.border};border-radius:6px;font-size:0.95rem;
  &:focus{outline:none;border-color:${p => p.theme.colors.primary}}
`;



/* ─── Current Hora Card ─── */
const CurrentHoraCard = styled.div`
  background:linear-gradient(135deg,${p => p.theme.colors.primary} 0%,${p => p.theme.colors.primary} 100%);color:${p => p.theme.colors.surface};border-radius: ${p => p.theme.radius.lg}px;
  padding:30px;text-align:center;margin-bottom:24px;animation:${pulse} 1.5s ease;
  box-shadow:0 4px 20px rgba(108,99,255,0.3);
`;
const CurrentHoraSymbol = styled.div`font-size:3.5rem;margin-bottom:8px;`;
const CurrentHoraName = styled.div`font-size:1.6rem;font-weight:700;`;
const CurrentHoraTime = styled.div`font-size:0.9rem;opacity:0.85;margin-top:6px;`;
const CurrentHoraActivity = styled.div`font-size:0.95rem;margin-top:12px;font-style:italic;opacity:0.9;`;

/* ─── Hora Grid ─── */
const HoraGrid = styled.div`
  display:grid;grid-template-columns:repeat(auto-fill,minmax(240px,1fr));gap:12px;margin-top:16px;
`;
const HoraCard = styled.div`
  background:${p => p.current ? p.theme.colors.background : p.theme.colors.surface};
  border:${p => p.current ? '2px solid ' + p.theme.colors.primary : '1px solid ' + p.theme.colors.border};
  border-radius: ${p => p.theme.radius.md}px;padding:16px;display:flex;align-items:center;gap:14px;
  animation:${fadeIn} 0.3s ease;
  ${p => p.current && 'box-shadow:0 2px 12px rgba(108,99,255,0.2);'}
`;
const HoraSymbol = styled.div`font-size:2rem;min-width:40px;text-align:center;`;
const HoraInfo = styled.div`flex:1;`;
const HoraName = styled.div`font-weight:700;font-size:0.95rem;color:${p => p.theme.colors.text.primary};`;
const HoraTime = styled.div`font-size:0.8rem;color:${p => p.theme.colors.text.muted};margin-top:2px;`;
const HoraActivitySmall = styled.div`font-size:0.78rem;color:${p => p.theme.colors.text.secondary};margin-top:4px;font-style:italic;`;
const HoraIndex = styled.div`font-size:0.7rem;color:${p => p.theme.colors.text.muted};font-weight:600;`;

/* ─── Section ─── */
const SectionTitle = styled.h3`font-size:1.1rem;color:${p => p.theme.colors.text.primary};margin:24px 0 12px;`;

const BackButton = styled.button`
  background:none;border:1px solid ${p => p.theme.colors.border};border-radius:6px;padding:8px 20px;
  font-size:0.85rem;cursor:pointer;color:${p => p.theme.colors.text.secondary};margin-bottom:20px;
  &:hover{border-color:${p => p.theme.colors.primary};color:${p => p.theme.colors.primary}}
`;

const planetSymbols = {
  sun: '\u2609', Sun: '\u2609',
  moon: '\u263D', Moon: '\u263D',
  mars: '\u2642', Mars: '\u2642',
  mercury: '\u263F', Mercury: '\u263F',
  jupiter: '\u2643', Jupiter: '\u2643',
  venus: '\u2640', Venus: '\u2640',
  saturn: '\u2644', Saturn: '\u2644',
};

const planetActivities = {
  Sun: 'Government work, leadership, authority matters, health',
  Moon: 'Travel, emotions, public dealing, creativity',
  Mars: 'Courage, competition, sports, surgery, mechanical work',
  Mercury: 'Communication, trade, education, writing, accounting',
  Jupiter: 'Spiritual activities, teaching, charity, wisdom',
  Venus: 'Arts, romance, beauty, luxury, entertainment',
  Saturn: 'Labour, agriculture, real estate, mining, discipline',
};

const langLabels = { en: 'English', ta: 'Tamil' };

/* ─── Component ─── */
const PlanetaryHora = () => {
  const [lang, setLang] = useState('en');
  const [place, setPlace] = useState('');
  const [lat, setLat] = useState(null);
  const [lon, setLon] = useState(null);
  const [timezone, setTimezone] = useState('Asia/Kolkata');
  const [date, setDate] = useState(new Date().toISOString().split('T')[0]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const handlePlaceChange = useCallback((data) => {
    setPlace(data.place);
    setLat(data.lat);
    setLon(data.lon);
    setTimezone(data.timezone);
  }, []);

  const fetchHora = useCallback(async () => {
    if (!lat || !lon) {
      setError('Please select a place from the suggestions to get coordinates.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const res = await axios.get('/vedic/v1/hora', {
        params: { lat, lon, date, tz: timezone, lang }
      });
      setResult(res.data);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to fetch hora data');
    } finally {
      setLoading(false);
    }
  }, [lat, lon, date, timezone, lang]);

  const getSymbol = (planet) => {
    if (!planet) return '';
    const key = planet.charAt(0).toUpperCase() + planet.slice(1).toLowerCase();
    return planetSymbols[key] || planetSymbols[planet] || '';
  };

  const getActivity = (planet) => {
    if (!planet) return '';
    const key = planet.charAt(0).toUpperCase() + planet.slice(1).toLowerCase();
    return planetActivities[key] || planetActivities[planet] || '';
  };

  const planetTamil = { 'Sun': 'சூரியன்', 'Moon': 'சந்திரன்', 'Mars': 'செவ்வாய்', 'Mercury': 'புதன்', 'Jupiter': 'குரு', 'Venus': 'சுக்கிரன்', 'Saturn': 'சனி' };
  const fmtHoraTime = (t) => { if (!t) return '--'; if (t.includes('T')) { try { return new Date(t).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', hour12: true, timeZone: 'Asia/Kolkata' }); } catch { return t; } } return t; };
  const pName = (p) => lang === 'ta' ? (planetTamil[p] || p) : p;

  const horasList = Array.isArray(result?.horas) ? result.horas : (result?.horas?.horas || []);
  const currentHora = result?.current_hora || horasList.find(h => h.is_current);
  const dayHoras = horasList.filter(h => h.period === 'day');
  const nightHoras = horasList.filter(h => h.period === 'night');
  const allHoras = dayHoras.length > 0 && nightHoras.length > 0 ? null : horasList;

  return (
    <PageContainer>
      <PageTitle>{lang === 'ta' ? 'கிரக ஹோரை' : 'Planetary Hora'}</PageTitle>
      <PageSubtitle>{lang === 'ta' ? '24 கிரக மணி நேரங்கள் - செயல்பாட்டு வழிகாட்டி' : '24 Planetary Hours with Activity Guide'}</PageSubtitle>

      <div style={{ textAlign: 'right', marginBottom: 10 }}>
        <Select value={lang} onChange={e => setLang(e.target.value)} style={{ width: 'auto', padding: '6px 10px' }}>
          {Object.entries(langLabels).map(([code, label]) => (
            <option key={code} value={code}>{label}</option>
          ))}
        </Select>
      </div>

      {!result ? (
        <TopGrid>
          <Card>
            <CardTitle>{lang === 'ta' ? 'இடம் & தேதி' : 'Location & Date'}</CardTitle>
            <SharedLocationForm
              placeValue={place}
              lat={lat}
              lon={lon}
              timezone={timezone}
              onPlaceChange={handlePlaceChange}
              showDate
              date={date}
              onDateChange={setDate}
              onSubmit={fetchHora}
              submitLabel={lang === 'ta' ? 'ஹோரை காட்டு' : 'Show Hora'}
              loading={loading}
              error={error}
              pageName="planetaryHora"
              lang={lang}
            />
          </Card>
          <Card>
            <CardTitle>{lang === 'ta' ? 'கிரக ஹோரை என்றால் என்ன?' : 'What is Planetary Hora?'}</CardTitle>
            <p style={{ color: '#555', lineHeight: 1.7, fontSize: '0.95rem' }}>
              {lang === 'ta'
                ? 'ஒவ்வொரு நாளும் 24 கிரக மணி நேரங்களாகப் பிரிக்கப்படுகிறது. ஒவ்வொரு மணி நேரமும் ஒரு கிரகத்தால் ஆளப்படுகிறது. சரியான ஹோரையில் செயல்களைத் தொடங்குவது வெற்றியை உறுதி செய்கிறது.'
                : 'Each day is divided into 24 planetary hours (hora). Each hour is ruled by a planet. Starting activities in the right hora ensures success. The sequence follows: Sun, Venus, Mercury, Moon, Saturn, Jupiter, Mars.'}
            </p>
            <div style={{ marginTop: 20, display: 'flex', flexWrap: 'wrap', gap: 10, justifyContent: 'center' }}>
              {Object.entries(planetSymbols).filter(([k]) => k[0] === k[0].toUpperCase()).map(([name, sym]) => (
                <div key={name} style={{ textAlign: 'center', padding: '8px 14px', background: '#f8f8ff', borderRadius: 8 }}>
                  <div style={{ fontSize: '1.5rem' }}>{sym}</div>
                  <div style={{ fontSize: '0.75rem', color: '#666', fontWeight: 600 }}>{name}</div>
                </div>
              ))}
            </div>
          </Card>
        </TopGrid>
      ) : (
        <>
          <BackButton onClick={() => setResult(null)}>
            {lang === 'ta' ? 'புதிய தேடல்' : 'New Search'}
          </BackButton>

          {/* Current Hora */}
          {currentHora && (
            <CurrentHoraCard>
              <CurrentHoraSymbol>{getSymbol(currentHora.planet || currentHora.lord)}</CurrentHoraSymbol>
              <CurrentHoraName>
                {lang === 'ta' ? 'தற்போதைய ஹோரை: ' : 'Current Hora: '}
                {pName(currentHora.planet || currentHora.lord)}
              </CurrentHoraName>
              <CurrentHoraTime>{fmtHoraTime(currentHora.start)} - {fmtHoraTime(currentHora.end)}</CurrentHoraTime>
              <CurrentHoraActivity>
                {currentHora.activity || getActivity(currentHora.planet || currentHora.lord)}
              </CurrentHoraActivity>
            </CurrentHoraCard>
          )}

          {/* Day Horas */}
          {dayHoras.length > 0 && nightHoras.length > 0 ? (
            <>
              <SectionTitle>{lang === 'ta' ? 'பகல் ஹோரைகள்' : 'Day Horas'}</SectionTitle>
              <HoraGrid>
                {dayHoras.map((h, i) => {
                  const planet = h.planet || h.lord || '';
                  const isCurrent = h.is_current || (currentHora && h.start === currentHora.start);
                  return (
                    <HoraCard key={i} current={isCurrent}>
                      <HoraSymbol>{getSymbol(planet)}</HoraSymbol>
                      <HoraInfo>
                        <HoraName>{pName(planet)}</HoraName>
                        <HoraTime>{fmtHoraTime(h.start)} - {fmtHoraTime(h.end)}</HoraTime>
                        <HoraActivitySmall>{h.activity || getActivity(planet)}</HoraActivitySmall>
                      </HoraInfo>
                      <HoraIndex>#{h.index || i + 1}</HoraIndex>
                    </HoraCard>
                  );
                })}
              </HoraGrid>

              <SectionTitle>{lang === 'ta' ? 'இரவு ஹோரைகள்' : 'Night Horas'}</SectionTitle>
              <HoraGrid>
                {nightHoras.map((h, i) => {
                  const planet = h.planet || h.lord || '';
                  const isCurrent = h.is_current || (currentHora && h.start === currentHora.start);
                  return (
                    <HoraCard key={i} current={isCurrent}>
                      <HoraSymbol>{getSymbol(planet)}</HoraSymbol>
                      <HoraInfo>
                        <HoraName>{pName(planet)}</HoraName>
                        <HoraTime>{fmtHoraTime(h.start)} - {fmtHoraTime(h.end)}</HoraTime>
                        <HoraActivitySmall>{h.activity || getActivity(planet)}</HoraActivitySmall>
                      </HoraInfo>
                      <HoraIndex>#{h.index || i + 1}</HoraIndex>
                    </HoraCard>
                  );
                })}
              </HoraGrid>
            </>
          ) : allHoras && allHoras.length > 0 ? (
            <>
              <SectionTitle>{lang === 'ta' ? 'அனைத்து ஹோரைகள்' : 'All 24 Horas'}</SectionTitle>
              <HoraGrid>
                {allHoras.map((h, i) => {
                  const planet = h.planet || h.lord || '';
                  const isCurrent = h.is_current || (currentHora && h.start === currentHora.start);
                  return (
                    <HoraCard key={i} current={isCurrent}>
                      <HoraSymbol>{getSymbol(planet)}</HoraSymbol>
                      <HoraInfo>
                        <HoraName>{pName(planet)}</HoraName>
                        <HoraTime>{fmtHoraTime(h.start)} - {fmtHoraTime(h.end)}</HoraTime>
                        <HoraActivitySmall>{h.activity || getActivity(planet)}</HoraActivitySmall>
                      </HoraInfo>
                      <HoraIndex>#{h.index || i + 1}</HoraIndex>
                    </HoraCard>
                  );
                })}
              </HoraGrid>
            </>
          ) : null}
        </>
      )}
    </PageContainer>
  );
};

export default PlanetaryHora;
